import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { formatMoney } from "../../../components/Utils/FormatUtils";

/**
 * Détail du calcul d'un départ en congé (Yros/Support#49), tel que le serveur
 * le renvoie : période et bruts retenus, formule, régularisation calculée,
 * jours. Affiché sur le départ en congé et depuis l'historique des congés.
 *
 * Un départ validé avant que les jours soient relevés ne connaît pas son
 * solde (`days_available` null) : seules les lignes connues s'affichent.
 */
const LeaveCalculationDetail = ({ detail, gestionType }) => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.resolvedLanguage?.startsWith('en') ? 'en-US' : 'fr-FR';
  const formatDate = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString(dateLocale) : '');
  const formatDays = (value) => Number(value || 0).toLocaleString(dateLocale, { maximumFractionDigits: 1 });
  const known = (value) => value !== null && value !== undefined;

  return (
    <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm">
      {gestionType !== "paid_leave_no_ic" && (
        <>
          <dt className="text-gray-500">{t('employeeProfile.leaveDeparture.detailPeriod')}</dt>
          <dd>{t('employeeProfile.leaveDeparture.detailPeriodValue', { start: formatDate(detail.period_start), end: formatDate(detail.period_end) })}</dd>
          <dt className="text-gray-500">{t('employeeProfile.leaveDeparture.detailTotalBrut')}</dt>
          <dd>{formatMoney(detail.total_brut || 0)}</dd>
          <dt className="text-gray-500">{t('employeeProfile.leaveDeparture.detailFormula')}</dt>
          <dd>{`${formatMoney(detail.total_brut || 0)} ÷ ${detail.divisor} = ${formatMoney(detail.computed_amount || 0)}`}</dd>
        </>
      )}
      {gestionType === "paid_leave_with_ic" && (
        <>
          <dt className="text-gray-500">{t('employeeProfile.leaveDeparture.detailRegularization')}</dt>
          <dd>{formatMoney(detail.regule_amount || 0)}</dd>
        </>
      )}
      {known(detail.days_available) && (
        <>
          <dt className="text-gray-500">{t('employeeProfile.leaveDeparture.detailDaysAvailable')}</dt>
          <dd>{formatDays(detail.days_available)}</dd>
        </>
      )}
      {known(detail.days_taken) && (
        <>
          <dt className="text-gray-500">{t('employeeProfile.leaveDeparture.detailDaysTaken')}</dt>
          <dd>{formatDays(detail.days_taken)}</dd>
        </>
      )}
      {known(detail.days_remaining) && (
        <>
          <dt className="text-gray-500">{t('employeeProfile.leaveDeparture.detailDaysRemaining')}</dt>
          <dd>{formatDays(detail.days_remaining)}</dd>
        </>
      )}
    </dl>
  );
};

LeaveCalculationDetail.propTypes = {
  detail: PropTypes.object.isRequired,
  gestionType: PropTypes.string,
};

export default LeaveCalculationDetail;
